/**
 * A time limit on App Check's reCAPTCHA, so a stuck security check can never
 * hold the whole Hub hostage (Bobbie, 2026-09-27: "Still loading too slowly.
 * White screen for a long time. Sometimes doesn't load for up to several
 * min.").
 *
 * WHAT WAS PROVEN, by running it, not by reading it:
 *   - @firebase/app-check 0.11.4 loads Google's reCAPTCHA script with an
 *     onload handler and NO onerror handler (loadReCAPTCHAEnterpriseScript in
 *     the SDK). If that script never arrives, the promise every token request
 *     waits on never settles. Against the live Hub with the reCAPTCHA hosts
 *     blocked, App Check's own token call was STILL PENDING after 90 seconds.
 *   - Firestore asks App Check for a token before every request and stream
 *     it opens, and waits for the answer with no time limit of its own
 *     (FirebaseAppCheckTokenProvider.getToken in @firebase/firestore).
 *     Firebase Auth waits on it too, including the account reload at
 *     startup. So one reCAPTCHA download that stalls on a weak phone signal,
 *     or that a content blocker refuses, freezes sign-in restore and every
 *     fresh read in the app: the class board, which deliberately waits for
 *     the server rather than showing the phone's saved copy, sits on
 *     "Loading" for as long as the stall lasts.
 *
 * WHO NEEDS A REAL TOKEN AND WHO DOES NOT:
 *   - Firestore, Storage and Auth do not enforce App Check: HUB-SETUP.md
 *     records it in monitor mode, and Auth was proven from here on
 *     2026-09-27 (createAuthUri answered normally with no token at all).
 *     Firestore and Storage could NOT be proven from here: a tokenless read
 *     comes back PERMISSION_DENIED, but an enforced project refuses with
 *     that same answer, so the test cannot tell the two apart (final
 *     review, 2026-09-28). The Firebase console (App Check, then APIs) is
 *     the authority, and it is Bobbie's. For all three, going ahead without
 *     a token costs only an "unverified" tick in App Check's monitoring.
 *   - About forty six Netlify Functions DO enforce it: they call
 *     appCheckGuard with `{ required: true }` (familyMessage, attachmentUpload,
 *     messageAttachmentWrite and more), so a call without a real token is
 *     refused with a 401. For those, going ahead early would turn a slow but
 *     working check into a refusal. lib/functionSecurity.ts therefore does
 *     NOT stop at this deadline: on the deadline error it waits for the
 *     attempt that is still running (whenSettled, below) and asks again.
 *
 * WHAT THIS DOES. A token request waits at most APP_CHECK_TOKEN_DEADLINE_MS
 * for reCAPTCHA. Past that, it is refused, and App Check hands Firestore,
 * Storage and Auth its own placeholder, exactly as it already does whenever
 * reCAPTCHA answers with an error. The reCAPTCHA attempt keeps running. Every
 * request that arrives while that one attempt is outstanding shares it and
 * shares its deadline, counted from when the attempt started. If the attempt
 * then succeeds LATE, its token is kept and handed to every request that asks
 * while the caller's `usable` test still passes, instead of being thrown
 * away, and onLateResult fires so the caller can ask App Check for a token
 * straight away: that is how Firestore and Auth move onto the real token
 * within moments of it arriving, rather than on App Check's next retry thirty
 * seconds later. It has to be shared for more than an instant, and for more
 * than a couple of seconds: refute round two measured App Check's own retry,
 * scheduled thirty seconds after the deadline refused it, asking again as a
 * FORCED refresh and paying for a second reCAPTCHA check at about 35
 * seconds (at the time, with the 5 second limit), and a third on a phone
 * that is always slow. lib/firebase.ts
 * shares it until the token's half-life, which is safe: App Check schedules
 * its next refresh at half the token's life plus five minutes, so handing
 * the same token to a forced refresh any time before the half-life always
 * lands that next refresh at least five minutes ahead, never in the past.
 * If the attempt never settles (the script never loaded), every later
 * request is refused at once, which is what the SDK would have done forever
 * anyway, minus the freeze.
 *
 * BEFORE ANYONE TURNS ON ENFORCEMENT for Firestore, Storage or Auth: a
 * request that goes out past the deadline carries the placeholder, so under
 * enforcement it would be refused instead of frozen. Revisit the deadline
 * then, and read this file first.
 *
 * Pure on purpose (no Firebase import) so tests/app-check-deadline.test.mjs
 * can drive it directly with fake attempts and a fake clock.
 */

/**
 * How long anyone waits on reCAPTCHA before Firestore and Auth carry on without it.
 * Was 5000 when this shipped (#1244); cut to 2000 on 2026-09-28 at Bobbie's word ("five
 * seconds even feels long in that moment") once she confirmed in the Firebase console that
 * Cloud Firestore and Cloud Storage are Unenforced, so going ahead early costs nothing but
 * an "unverified" tick in App Check's monitoring. Function calls still wait for the real
 * token (lib/functionSecurity.ts); the cut only moves their longest wait before a clear
 * refusal from 35 to 32 seconds.
 */
export const APP_CHECK_TOKEN_DEADLINE_MS = 2000;

const DEADLINE_ERROR_CODE = 'hub/app-check-deadline';

export type AppCheckDeadlineError = Error & { code: typeof DEADLINE_ERROR_CODE };

function deadlineError(ms: number): AppCheckDeadlineError {
  const error = new Error(
    `App Check did not answer within ${ms}ms; continuing without a token while it keeps trying.`
  ) as AppCheckDeadlineError;
  error.code = DEADLINE_ERROR_CODE;
  return error;
}

export function isAppCheckDeadlineError(error: unknown): error is AppCheckDeadlineError {
  return (
    typeof error === 'object'
    && error !== null
    && (error as { code?: unknown }).code === DEADLINE_ERROR_CODE
  );
}

// A token that arrived late is shared until its half-life, then dropped.
// App Check schedules its next refresh at half the token's life plus five
// minutes (the Refresher in @firebase/app-check), so handing the same token
// to one of its forced refreshes any time before the half-life lands the next
// refresh at least five minutes ahead, never in the past; after the
// half-life, the refresh gets a fresh check, as it would have without this.
// The minimum-life test is a second belt.
export const LATE_TOKEN_MIN_LIFE_MS = 60_000;

/** Whether an App Check token that arrived late may still be handed out. */
export function appCheckTokenShareable(value: unknown, nowMs: number): boolean {
  const { expireTimeMillis, issuedAtTimeMillis } =
    (value as { expireTimeMillis?: unknown; issuedAtTimeMillis?: unknown } | null) ?? {};
  if (typeof expireTimeMillis !== 'number' || typeof issuedAtTimeMillis !== 'number') return false;
  const halfLife = issuedAtTimeMillis + (expireTimeMillis - issuedAtTimeMillis) / 2;
  return nowMs < halfLife && expireTimeMillis - nowMs > LATE_TOKEN_MIN_LIFE_MS;
}

export type Timers = {
  setTimeout: (callback: () => void, ms: number) => unknown;
  clearTimeout: (handle: unknown) => void;
  now: () => number;
};

const realTimers: Timers = {
  setTimeout: (callback, ms) => globalThis.setTimeout(callback, ms),
  clearTimeout: (handle) => globalThis.clearTimeout(handle as ReturnType<typeof setTimeout>),
  now: () => Date.now(),
};

export type TokenDeadlineOptions<T> = {
  deadlineMs?: number;
  timers?: Timers;
  /**
   * Whether a result that arrived late may still be handed out, asked again
   * on every request, so it decides how long the late result is shared. The
   * caller knows what a token looks like; this module does not. Without it a
   * late result is never kept (onLateResult still fires).
   */
  usable?: (value: T) => boolean;
  /** Called once when an attempt succeeds after its deadline had passed. */
  onLateResult?: () => void;
};

export type TokenDeadline<T> = {
  /**
   * Waits on `start()` for at most the deadline. `start` is only called when
   * no attempt is outstanding and no late result is being shared, so a slow
   * attempt is shared, never duplicated.
   */
  run(start: () => Promise<T>): Promise<T>;
  /**
   * Resolves when the outstanding attempt settles, or once that attempt has
   * been running for its deadline plus `extraMs`, whichever is first. The
   * budget is counted from when the ATTEMPT started, not from this call, so
   * on a page whose reCAPTCHA never loads only the first caller pays for the
   * wait and every later one is released at once. Resolves at once when
   * nothing is outstanding. Never rejects. For callers whose server refuses a
   * request without a real token.
   */
  whenSettled(extraMs: number): Promise<void>;
};

export function createTokenDeadline<T>(options: TokenDeadlineOptions<T> = {}): TokenDeadline<T> {
  const deadlineMs = options.deadlineMs ?? APP_CHECK_TOKEN_DEADLINE_MS;
  const timers = options.timers ?? realTimers;
  const usable = options.usable ?? (() => false);

  // The one outstanding reCAPTCHA attempt, if any.
  let current: { attempt: Promise<T>; expired: Promise<never>; late: boolean; startedAt: number } | null = null;
  // A token that arrived after its deadline, shared while `usable` says so.
  let lateResult: { value: T } | null = null;

  return {
    run(start) {
      if (!current && lateResult) {
        if (usable(lateResult.value)) return Promise.resolve(lateResult.value);
        lateResult = null;
      }
      if (!current) {
        let attempt: Promise<T>;
        try {
          attempt = start();
        } catch (error) {
          return Promise.reject(error);
        }
        let expire: (error: AppCheckDeadlineError) => void = () => {};
        const expired = new Promise<never>((_resolve, reject) => {
          expire = reject;
        });
        // Nobody may be racing it when it fires; that is not an error.
        expired.catch(() => {});
        const entry = { attempt, expired, late: false, startedAt: timers.now() };
        const handle = timers.setTimeout(() => {
          entry.late = true;
          expire(deadlineError(deadlineMs));
        }, deadlineMs);
        attempt.then(
          (value) => {
            timers.clearTimeout(handle);
            if (current === entry) current = null;
            if (entry.late) {
              if (usable(value)) lateResult = { value };
              options.onLateResult?.();
            }
          },
          () => {
            timers.clearTimeout(handle);
            if (current === entry) current = null;
          },
        );
        current = entry;
      }
      // Once the deadline has passed, `expired` is already rejected, so a
      // request arriving after that is refused on the next tick rather than
      // made to wait another full deadline.
      return Promise.race([current.attempt, current.expired]);
    },

    whenSettled(extraMs) {
      const outstanding = current;
      if (!outstanding) return Promise.resolve();
      const remaining = outstanding.startedAt + deadlineMs + extraMs - timers.now();
      if (remaining <= 0) return Promise.resolve();
      return new Promise<void>((resolve) => {
        const handle = timers.setTimeout(resolve, remaining);
        const done = () => {
          timers.clearTimeout(handle);
          resolve();
        };
        outstanding.attempt.then(done, done);
      });
    },
  };
}

// The one App Check provider this page runs registers its deadline here, so
// lib/functionSecurity.ts can wait on it. It lives in this module rather than
// in lib/firebase.ts on purpose: dozens of tests replace lib/firebase.ts with
// a stand-in, and a new export there would have to be added to every one.
let activeDeadline: TokenDeadline<unknown> | null = null;

export function registerActiveTokenDeadline(deadline: TokenDeadline<unknown>): void {
  activeDeadline = deadline;
}

/**
 * For callers whose SERVER refuses a request without a real App Check token
 * (the Netlify Functions that pass `required: true` to appCheckGuard): after
 * App Check's getToken gives up at the deadline, wait for the reCAPTCHA
 * attempt that is still running, until it has been running for its deadline
 * plus `extraMs` (see whenSettled), then ask again. Resolves, never rejects,
 * and resolves at once when nothing is outstanding.
 */
export function waitForAppCheckAttempt(extraMs: number): Promise<void> {
  return activeDeadline ? activeDeadline.whenSettled(extraMs) : Promise.resolve();
}

/**
 * RECAPTCHA LOADS ONCE, AND ONLY WHEN A TOKEN IS FIRST NEEDED (speed plan
 * 2026-10-19, P2; audit findings A-03, A-13 and A-19). The SDK's reCAPTCHA
 * providers add Google's script to the page from initialize(), which App
 * Check calls inside initializeAppCheck() at import time, so every open paid
 * for about 350 KB (the biggest download of a cold open) whether or not
 * anything on screen needed a token: the signed-out home, a dancer's own
 * page, and a returning person whose saved token was still good.
 *
 * lib/firebase.ts holds that initialize() back with this gate and releases it
 * on the provider's first getToken(). App Check only asks the provider when
 * it has no usable token of its own (memory or the saved copy), and every
 * caller that needs one (Firestore, Storage, Auth, and lib/functionSecurity.ts
 * for the Netlify Functions) asks through App Check, so the order is always:
 * the call asks, the script is added (once), the token request waits for it
 * under the same deadline as before, the call goes out. Nothing is skipped.
 *
 * Pure on purpose, so tests/app-check-deadline.test.mjs drives it directly.
 */
export type LazyStart = {
  /** Store the work to do on the first release. Called once, by initialize(). */
  hold(start: () => void): void;
  /** Run the held work if it has not run yet. True only on the call that ran it. */
  release(): boolean;
  readonly started: boolean;
};

export function createLazyStart(): LazyStart {
  let held: (() => void) | null = null;
  let started = false;
  return {
    hold(start) {
      held = start;
    },
    release() {
      if (started || !held) return false;
      started = true;
      const run = held;
      held = null;
      run();
      return true;
    },
    get started() {
      return started;
    },
  };
}
